import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FaCheck, FaEdit, FaEye, FaMoneyBillWave } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { BsThreeDotsVertical } from "react-icons/bs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeading from "../../PageHeading";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import ExportOptions from "../../ExportOptions";
import axios from "axios";
import { useAuth } from "../../../utils/AuthContext";
import TableSkeleton from "../../TableSkeleton";
import OutdoorViewBillModal from "./OutdoorViewBillModal";
import OutdoorBillModal from "./OutdoorBillModal";
import OutdoorAddPaymentModal from "./OutdoorAddPaymentModal";
import DynamicSelect from "../../DynamicSelect";
import SmartDropdown from "../../Shared/SmartDropdown";

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api/outdoor-bill";

const OutdoorBill = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  
  // Modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [generateBillModalOpen, setGenerateBillModalOpen] = useState(false);
  const [viewBillModalOpen, setViewBillModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  // Data States
  const [viewBillData, setViewBillData] = useState(null);
  const [editBillId, setEditBillId] = useState(null);
  const [paymentBillData, setPaymentBillData] = useState(null);
  const [bills, setBills] = useState([]);
  
  // Search & Pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Filters
  const [filterType, setFilterType] = useState("monthly");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  // Dropdown Refs
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownTriggerRefs = useRef({});

  const itemsPerPage = 10;
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const actionButtonClass = "size-7 rounded-full text-sm flex items-center justify-center transition-all duration-300";
  const { user } = useAuth();

  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  // --- Fetch Bills ---
  const fetchBills = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchQuery,
        type: filterType
      };

      if (filterType === 'daily') {
         params.startDate = startDate;
         params.endDate = endDate;
      } else if (filterType === 'monthly') {
         const [y, m] = selectedMonth.split('-');
         params.year = y;
         params.month = m;
      } else if (filterType === 'yearly') {
         params.year = selectedYear;
      }

      const response = await axios.get(BASE_URL, {
        headers: { "x-api-key": API_KEY },
        params: params
      });

      const { bills, total } = response.data;
      
      setBills(bills || []);
      setTotalRecords(total || 0);
      setTotalPages(Math.ceil(total / itemsPerPage) || 1);

    } catch (err) {
      console.error("Error fetching bills:", err);
      toast.error("Failed to load bills.");
      setBills([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearchQuery, itemsPerPage, filterType, startDate, endDate, selectedMonth, selectedYear]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, filterType, startDate, endDate, selectedMonth, selectedYear]);

  // --- Handlers ---

  const handleRefreshBills = useCallback(async () => {
    await fetchBills();
  }, [fetchBills]);

  const handleDelete = useCallback(
    async (billIds) => {
      try {
        await Promise.all(
          billIds.map((id) =>
            axios.delete(`${BASE_URL}/delete-payment/${id}`, {
               url: `${BASE_URL}/delete/${id}`,
               headers: { "x-api-key": API_KEY },
            })
          )
        );
        await handleRefreshBills();
        setSelectedRows([]);
        toast.success(`${billIds.length} bill(s) deleted successfully!`);
      } catch (err) {
        console.error("Error deleting bills:", err);
        toast.error("Failed to delete bill(s).");
      }
    },
    [handleRefreshBills]
  );

  const handleEdit = useCallback((bill) => {
    setEditBillId(bill.id);
    setGenerateBillModalOpen(true);
  }, []);

  const handleView = useCallback((bill) => {
    setViewBillData(bill);
    setViewBillModalOpen(true);
  }, []);

  // ✅ Add Payment Handler
  const handleAddMoney = (bill) => {
    setPaymentBillData(bill);
    setPaymentModalOpen(true);
  };

  const handlePageChange = useCallback(
    (page) => {
      if (page > 0 && page <= totalPages) {
        setCurrentPage(page);
        setSelectedRows([]);
      }
    },
    [totalPages]
  );

  const handleSelectAll = useCallback(
    (e) => {
      const validIds = bills.map((bill) => bill.id);
      setSelectedRows(e.target.checked ? validIds : []);
    },
    [bills]
  );

  const handleRowSelect = useCallback((billId) => {
    setSelectedRows((prev) =>
      prev.includes(billId)
        ? prev.filter((id) => id !== billId)
        : [...prev, billId]
    );
  }, []);

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  // --- Configuration ---

  const headers = [
    "SL No",
    "BILL NO",
    "DATE",
    "PATIENT NAME",
    "PHONE",
    "NET AMOUNT (৳)",
    "PAID (৳)",
    "DUE (৳)",
    "REFER",
  ];

  const dataMapper = (bill) => {
    const net = bill.netAmount || 0;
    const paid = bill.paidAmount || 0;
    const due = net - paid;
    
    return [
      (currentPage - 1) * itemsPerPage + bills.indexOf(bill) + 1,
      bill.billNumber || "-",
      new Date(bill.createdAt).toLocaleDateString("en-GB"),
      bill.patientName || "-",
      bill.phoneNumber || "-",
      net.toFixed(2),
      paid.toFixed(2),
      due.toFixed(2),
      bill.refer || "N/A",
    ].join('\t');
  };

  const skeletonColumns = [
    { width: 20, height: 20, isCircle: true, className: "print-hidden-in-print" },
    { width: 50, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 150, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 120, height: 16, className: "print-hidden-in-print" },
  ];

  const breadcrumbs = useMemo(() => [
      { label: "Dashboard", url: "/" },
      { label: "Hospital Office" },
      { label: "Outdoor Bills" },
    ], []
  );

  const allVisibleSelected =
    bills.length > 0 && bills.every((bill) => selectedRows.includes(bill.id));

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="Outdoor Bills" breadcrumbs={breadcrumbs} />
      </div>

      <div className="bg-white shadow-sm rounded-lg">
        {/* Header Controls */}
        <div className="flex justify-between flex-wrap border-b border-dashed border-M-text-color/50 p-4 gap-4 print-hidden">
          {/* Top Row */}
          <div className="flex items-center justify-between gap-3">
             <div className="inline-flex items-center gap-3">
               <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">Bill List</h2>
               {selectedRows.length > 0 && (
                 <button onClick={() => setDeleteModalOpen(true)} className={`${actionButtonClass} bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white`}>
                   <RiDeleteBin6Line />
                 </button>
               )}
             </div>
             <input
               type="text"
               className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color"
               placeholder="Search bill no..."
               value={searchQuery}
               onChange={(e) => setSearchQuery(e.target.value)}
             />
          </div>

          {/* Bottom Row: Filters & Actions */}
          <div className="flex flex-col sm:flex-row items-start justify-start sm:justify-end gap-3 w-full sm:w-auto">
             
             <div className="flex items-center gap-2 flex-wrap">
                <div className="w-32">
                  <DynamicSelect 
                    options={filterOptions} 
                    value={filterOptions.find(o => o.value === filterType)} 
                    onChange={o => setFilterType(o.value)} 
                    isSearchable={false}
                  />
                </div>

                {filterType === "daily" && (
                 <>
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-600">From:</label>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="p-2 border rounded-md text-sm outline-none" />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-600">To:</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="p-2 border rounded-md text-sm outline-none" />
                      </div>
                  </>
                )}
                {filterType === "monthly" && (
                  <div className="flex items-center gap-2">
                      <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="p-2 border rounded-md text-sm outline-none" />
                  </div>
                )}
                {filterType === "yearly" && (
                   <div className="flex items-center gap-2">
                      <input type="number" value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="p-2 border rounded-md text-sm outline-none w-24" min="2000" max="2100" />
                   </div>
                )}
             </div>

             <div className="flex items-center justify-between gap-3 sm:w-auto w-full">
                <button
                  onClick={() => { setEditBillId(null); setGenerateBillModalOpen(true); }}
                  className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 px-4 py-2 text-sm"
                >
                  + Outdoor Bill
                </button>
                <ExportOptions 
                   data={bills} 
                   dropdownOpen={dropdownOpen} 
                   toggleDropdown={toggleDropdown}
                   headers={headers} 
                   dataMapper={dataMapper}
                   title="Outdoor Bills"
                />
             </div>
          </div>
        </div>

        <style>{`@media print { .print-hidden, .print-hidden-in-print { display: none !important; } .print-table { width: 100%; border-collapse: collapse; } .print-table th, .print-table td { border: 1px solid #000; padding: 8px; font-size: 12px; } }`}</style>

        {/* Table */}
        <div className="overflow-x-auto">
          <div className="print-wrapper">
            <table className="min-w-[1440px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left print-hidden-in-print p-3">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        className="hidden peer"
                        onChange={handleSelectAll}
                        checked={allVisibleSelected}
                        id="selectAll"
                        disabled={bills.length === 0}
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label htmlFor="selectAll" className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"></label>
                    </div>
                  </th>
                  {headers.map((h, i) => (
                    <th key={i} className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                      {h}
                    </th>
                  ))}
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <TableSkeleton rowCount={itemsPerPage} columns={skeletonColumns} />
                ) : bills.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-5 text-gray-500">
                      No bills found.
                    </td>
                  </tr>
                ) : (
                  bills.map((bill, index) => {
                    const due = (bill.netAmount || 0) - (bill.paidAmount || 0);
                    return (
                      <tr key={bill.id} className="border-b hover:bg-gray-50">
                        <td className="text-left print-hidden-in-print p-3">
                          <div className="relative left-3 -top-[2px]">
                            <input
                              type="checkbox"
                              className="hidden peer"
                              id={`row-${bill.id}`}
                              checked={selectedRows.includes(bill.id)}
                              onChange={() => handleRowSelect(bill.id)}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label htmlFor={`row-${bill.id}`} className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"></label>
                          </div>
                        </td>
                        <td className="p-3 text-sm">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td className="p-3 text-sm">{bill.billNumber}</td>
                        <td className="p-3 text-sm">{new Date(bill.createdAt).toLocaleDateString("en-GB")}</td>
                        <td className="p-3 text-sm">{bill.patientName}</td>
                        <td className="p-3 text-sm">{bill.phoneNumber}</td>
                        <td className="p-3 text-sm">{(bill.netAmount || 0).toFixed(2)}</td>
                        <td className="p-3 text-sm">{(bill.paidAmount || 0).toFixed(2)}</td>
                        <td className="p-3 text-sm">{due > 0 ? due.toFixed(2) : "0.00"}</td>
                        <td className="p-3 text-sm">{bill.refer || "N/A"}</td>
                        <td className="p-3 text-right print-hidden-in-print">
                           {/* ✅ SMART DROPDOWN LOGIC */}
                           <div className="relative">
                              <button
                                ref={(el) => (dropdownTriggerRefs.current[bill.id] = el)}
                                onClick={() => setActiveDropdown(activeDropdown === bill.id ? null : bill.id)}
                                className="size-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-500"
                              >
                                <BsThreeDotsVertical size={14} />
                              </button>

                              <SmartDropdown
                                isOpen={activeDropdown === bill.id}
                                triggerRef={{ current: dropdownTriggerRefs.current[bill.id] }}
                                onClose={() => setActiveDropdown(null)}
                                options={{ width: 160, align: "right" }}
                              >
                                <div className="py-1">
                                  {/* Add Payment Button */}
                                  <button
                                    onClick={() => {
                                      handleAddMoney(bill);
                                      setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                                  >
                                    <FaMoneyBillWave className="text-green-600" size={14} />
                                    <span>Add Money</span>
                                  </button>

                                  <button
                                    onClick={() => { handleView(bill); setActiveDropdown(null); }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <FaEye className="text-blue-500" /> View Details
                                  </button>

                                  <button
                                    onClick={() => { handleEdit(bill); setActiveDropdown(null); }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <FaEdit className="text-green-600" /> Edit Bill
                                  </button>

                                  <button
                                    onClick={() => { setSelectedRows([bill.id]); setDeleteModalOpen(true); setActiveDropdown(null); }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 flex items-center gap-2"
                                  >
                                    <RiDeleteBin6Line className="text-red-500" /> Delete
                                  </button>
                                </div>
                              </SmartDropdown>
                           </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
              <div className="inline-flex items-center justify-between w-full">
                <p className="text-sm text-gray-600">
                  Records: {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords}
                </p>
                <nav aria-label="Pagination">
                  <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                    <li>
                      <button
                        className={`table-pagination-item ${currentPage === 1 ? "disabled" : ""}`}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <MdKeyboardArrowLeft />
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, i) => (
                       <li key={i}>
                         <button
                           className={`table-pagination-item ${currentPage === i + 1 ? "bg-M-primary-color text-white" : ""}`}
                           onClick={() => handlePageChange(i + 1)}
                         >
                           {i + 1}
                         </button>
                       </li>
                    ))}
                    <li>
                      <button
                        className={`table-pagination-item ${currentPage === totalPages ? "disabled" : ""}`}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <MdKeyboardArrowRight />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => handleDelete(selectedRows)}
        ids={selectedRows}
        entityName="bill"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
        message={`Delete ${selectedRows.length} bill(s)?`}
      />

      <OutdoorBillModal
        isOpen={generateBillModalOpen}
        onClose={() => {
          setGenerateBillModalOpen(false);
          setEditBillId(null);
        }}
        onSave={handleRefreshBills}
        billId={editBillId}
      />

      <OutdoorViewBillModal
        isOpen={viewBillModalOpen}
        onClose={() => {
          setViewBillModalOpen(false);
          setViewBillData(null);
        }}
        bill={viewBillData}
        onEdit={(bill) => {
          setEditBillId(bill.id);
          setGenerateBillModalOpen(true);
        }}
        onDelete={(id) => {
          setSelectedRows([id]);
          setDeleteModalOpen(true);
        }}
      />

      {/* ✅ Add Payment Modal */}
      <OutdoorAddPaymentModal
        isOpen={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setPaymentBillData(null);
        }}
        bill={paymentBillData}
        onPaymentSuccess={handleRefreshBills}
      />
    </div>
  );
};

export default OutdoorBill;